import React, { useEffect, useRef } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import IconActionContextRow from "../ui/IconActionContextRow";
import { INSPECTOR_SECTION_TITLE_TO_BODY_PAD_CLASS } from "../../utils/inspectorChromeButtonClasses";

const QUICK_NAV_HOVER_BUTTON_CLASS = "hover:!bg-[var(--panel-surface-hover)]";

const mergeButtonClassName = (baseClassName, extraClassName) =>
  [baseClassName, extraClassName].filter(Boolean).join(" ");

const QuickNavSection = ({
  title = "Tree Navigation",
  actions = [],
  headerActions = null,
  contentAfterRow = null,
  activeSubmenuKey = null,
  setActiveSubmenuKey,
  submenuClassName,
  submenuStyle,
  onDismissContentAfterRow,
}) => {
  const rootRef = useRef(null);
  const normalizedActions = (actions || []).filter(Boolean).map((action) => ({
    ...action,
    buttonClassName: mergeButtonClassName(QUICK_NAV_HOVER_BUTTON_CLASS, action?.buttonClassName),
  }));

  useEffect(() => {
    const hasOpenContent = Boolean(contentAfterRow);
    const hasOpenSubmenu = Boolean(activeSubmenuKey);
    if (!hasOpenContent && !hasOpenSubmenu) return undefined;

    const dismiss = () => {
      if (setActiveSubmenuKey) setActiveSubmenuKey(null);
      if (onDismissContentAfterRow) onDismissContentAfterRow();
    };

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      dismiss();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };

    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [activeSubmenuKey, contentAfterRow, onDismissContentAfterRow, setActiveSubmenuKey]);

  return (
    <Collapsible
      defaultOpen
      ref={rootRef}
      className={activeSubmenuKey ? "relative z-[1000]" : undefined}
    >
      <CollapsibleTrigger quiet actions={headerActions} aria-label={`Toggle ${title}`}>
        <span className="min-w-0 truncate text-left">{title}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="!overflow-visible">
        <div className={INSPECTOR_SECTION_TITLE_TO_BODY_PAD_CLASS}>
          <div className="inline-flex w-fit max-w-full items-center rounded-md border border-gray-600 p-0.5">
            <IconActionContextRow
              activeSubmenuKey={activeSubmenuKey}
              setActiveSubmenuKey={setActiveSubmenuKey}
              submenuClassName={submenuClassName}
              submenuStyle={submenuStyle}
              actions={normalizedActions}
            />
          </div>
          {contentAfterRow}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default React.memo(QuickNavSection);
